// Dinner in 3D (plazmi.kitchen). While a dinner is on, the round table sets
// itself: the fruit bowl steps aside, a place is laid for everyone at the
// table (more chairs come if there are more than two), each with what's being
// served, the pot or the cake in the middle steaming, and two candles lit.
// The resident and its guests walk to their places, sit and eat, and little
// hearts rise now and then. After the evening the plates stay a while, empty.
// A dish cooking on the range steams too.
import * as THREE from 'three';
import { mat, cyl, sphere, rbox, lathe } from './kit.js';
import { chair } from './furniture.js';
import { FLOOR, type House, type Thing } from './furnish.js';
import type { Life } from './life.js';

type P = [number, number, number];
export type DinnerLive = { at: number; until: number; on: boolean; dishes: string[]; table: string; guests: { slug: string; name: string }[]; ended?: number | null };
export type KitchenLive = { name: string; ready: number; portions: number; verb: string }[];

const FACING: Record<string, number> = { south: 0, east: Math.PI / 2, west: -Math.PI / 2, north: Math.PI };
// the places round a round table, in the table's own frame: the two chairs it
// comes with (west and east), then north and south
const PLACES: [number, number][] = [[-0.85, 0], [0.85, 0], [0, -0.85], [0, 0.85]];

// what a dish looks like in a bowl or on a plate
function food(name: string): { kind: 'soup' | 'stew' | 'stack' | 'omelette' | 'cake' | 'tart' | 'bread' | 'biscuits'; color: string } {
  const n = name.toLowerCase();
  if (n.includes('soup')) return { kind: 'soup', color: n.includes('pumpkin') ? '#e8913a' : n.includes('tomato') ? '#d9543a' : n.includes('pea') ? '#9cc45e' : n.includes('root') ? '#c98a4a' : '#d98b45' };
  if (n.includes('stew')) return { kind: 'stew', color: '#8a5a3a' };
  if (n.includes('pancake')) return { kind: 'stack', color: '#e8c27a' };
  if (n.includes('omelette')) return { kind: 'omelette', color: '#f2d36b' };
  if (n.includes('cake')) return { kind: 'cake', color: n.includes('apple') ? '#c9853a' : '#e8c58a' };
  if (n.includes('tart') || n.includes('pie')) return { kind: 'tart', color: n.includes('cherry') ? '#b3263a' : '#d4a05a' };
  if (n.includes('bread') || n.includes('loaf')) return { kind: 'bread', color: '#c8894a' };
  return { kind: 'biscuits', color: '#e9c98a' };
}

function plate(full: string | null) {
  const g = new THREE.Group();
  const china = mat('#f7f3ec', { rough: 0.25 });
  g.add(cyl(0.11, 0.09, 0.014, china, 24).translateY(0.007));
  if (full) {
    const f = food(full);
    if (f.kind === 'soup' || f.kind === 'stew') {
      g.add(lathe([[0, 0], [0.05, 0], [0.075, 0.035], [0.08, 0.05]], china, 20).translateY(0.012));
      g.add(cyl(0.07, 0.07, 0.006, mat(f.color, { rough: 0.35 }), 20).translateY(0.052));
      if (f.kind === 'stew') for (let i = 0; i < 4; i++) g.add(sphere(0.014, mat(['#e08a3a', '#9cc45e'][i % 2]), 6, 5).translateX(Math.cos(i * 1.6) * 0.035).translateY(0.058).translateZ(Math.sin(i * 1.6) * 0.035));
      else g.add(sphere(0.01, mat('#f6f0e4'), 6, 5).translateY(0.056).translateX(0.015));
    } else if (f.kind === 'stack') {
      for (let i = 0; i < 3; i++) g.add(cyl(0.06, 0.062, 0.014, mat(f.color, { rough: 0.6 }), 18).translateY(0.022 + i * 0.015));
      g.add(cyl(0.035, 0.035, 0.006, mat('#b5702a', { rough: 0.2 }), 14).translateY(0.065));
    } else if (f.kind === 'omelette') {
      const o = sphere(0.07, mat(f.color, { rough: 0.6 }), 14, 8); o.scale.set(1.2, 0.18, 0.7); o.position.y = 0.022; g.add(o);
    } else if (f.kind === 'bread') {
      for (let i = 0; i < 2; i++) { const s = rbox(0.07, 0.016, 0.06, 0.008, mat('#efd8a8', { rough: 0.8 }), 2); s.position.set(-0.02 + i * 0.04, 0.022 + i * 0.012, 0); s.rotation.y = i * 0.3; g.add(s); }
    } else {
      // a slice of cake or tart, or a few biscuits
      if (f.kind === 'biscuits') for (let i = 0; i < 3; i++) g.add(cyl(0.025, 0.025, 0.01, mat(f.color, { rough: 0.8 }), 12).translateX(-0.03 + i * 0.03).translateY(0.02 + (i % 2) * 0.006));
      else { const s = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.05, 12, 1, false, 0, 0.8), mat(f.kind === 'tart' ? '#e3b46a' : '#f0d9a8', { rough: 0.7 })); s.position.y = 0.04; g.add(s);
        const top = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.012, 12, 1, false, 0, 0.8), mat(f.color, { rough: 0.5 })); top.position.y = 0.07; g.add(top); }
    }
  }
  // a fork and a knife either side
  for (const s of [-1, 1]) { const c = rbox(0.012, 0.004, 0.13, 0.002, mat('#c9c4bb', { rough: 0.3, metal: 0.7 }), 1); c.position.set(s * 0.14, 0.004, 0); g.add(c); }
  return g;
}

// the dish in the middle: a pot of soup, a cake on a stand, a loaf on a board
function centrepiece(name: string) {
  const g = new THREE.Group(), f = food(name);
  if (f.kind === 'soup' || f.kind === 'stew') {
    const pot = mat('#5f7d6e', { rough: 0.4, metal: 0.2 });
    g.add(cyl(0.13, 0.11, 0.14, pot, 22).translateY(0.07));
    g.add(cyl(0.118, 0.118, 0.006, mat(f.color, { rough: 0.3 }), 22).translateY(0.13));
    for (const s of [-1, 1]) g.add(rbox(0.05, 0.02, 0.03, 0.008, pot, 1).translateX(s * 0.15).translateY(0.11));
    const ladle = cyl(0.008, 0.008, 0.22, mat('#c9c4bb', { metal: 0.7, rough: 0.3 }), 6); ladle.position.set(0.05, 0.2, 0); ladle.rotation.z = -0.5; g.add(ladle);
    g.userData.steam = 0.16;
  } else if (f.kind === 'cake' || f.kind === 'tart') {
    const china = mat('#f7f3ec', { rough: 0.25 });
    g.add(cyl(0.03, 0.05, 0.08, china, 14).translateY(0.04));
    g.add(cyl(0.15, 0.15, 0.012, china, 24).translateY(0.086));
    g.add(cyl(0.12, 0.12, f.kind === 'cake' ? 0.09 : 0.04, mat(f.kind === 'tart' ? '#e3b46a' : '#f0d9a8', { rough: 0.7 }), 24).translateY(f.kind === 'cake' ? 0.137 : 0.112));
    g.add(cyl(0.122, 0.122, 0.014, mat(f.color, { rough: 0.5 }), 24).translateY(f.kind === 'cake' ? 0.186 : 0.137));
    if (f.kind === 'cake') for (let i = 0; i < 7; i++) g.add(sphere(0.016, mat('#e2a23b', { rough: 0.4 }), 6, 5).translateX(Math.cos(i * 0.9) * 0.08).translateY(0.196).translateZ(Math.sin(i * 0.9) * 0.08));
    g.userData.steam = f.kind === 'cake' ? 0.22 : 0.16;
  } else {
    g.add(rbox(0.3, 0.025, 0.18, 0.01, mat('#a87a4f', { rough: 0.8 }), 2).translateY(0.0125));
    const loaf = rbox(0.2, 0.09, 0.12, 0.045, mat(f.color, { rough: 0.8 }), 3); loaf.position.y = 0.07; g.add(loaf);
    for (let i = 0; i < 3; i++) { const cut = rbox(0.008, 0.02, 0.1, 0.004, mat('#e8c890', { rough: 0.8 }), 1); cut.position.set(-0.05 + i * 0.05, 0.115, 0); g.add(cut); }
    g.userData.steam = 0.13;
  }
  return g;
}

function candle(x: number, z: number) {
  const g = new THREE.Group();
  g.add(cyl(0.035, 0.04, 0.03, mat('#c9a24a', { metal: 0.6, rough: 0.3 }), 12).translateY(0.015));
  g.add(cyl(0.016, 0.016, 0.16, mat('#f6f0e4', { rough: 0.6 }), 10).translateY(0.11));
  const flame = sphere(0.016, mat('#ffd36b', { emissive: '#ffb84a', emissiveIntensity: 4 }), 8, 6); flame.scale.set(0.8, 1.6, 0.8); flame.position.y = 0.21; flame.userData.noShadow = true; flame.userData.flame = true; g.add(flame);
  g.position.set(x, 0, z);
  return g;
}

export class Dinner {
  root = new THREE.Group();
  private h: House;
  private lot: THREE.Object3D;
  private live: { dinner?: DinnerLive | null; kitchen?: KitchenLive; acted?: { verb: string; object: string; at: number } | null; now: number } | null = null;
  private offset = 0;
  private key = '';
  private setting: THREE.Group | null = null;
  private steamAt: THREE.Vector3[] = [];
  private puff = 0; private heart = 3;
  emit: ((kind: string, at: THREE.Vector3, data?: any) => void) | null = null;
  // whether a plazmi sitting at a place has room (walls and other furniture clear), from life.ts
  private fits: ((p: P, table: string) => boolean) | null = null;

  constructor(h: House, lot: THREE.Object3D) {
    this.h = h; this.lot = lot;
    this.root.userData.noShadow = true;
  }

  setHouse(h: House, lot: THREE.Object3D) { this.h = h; this.lot = lot; this.key = ''; if (this.live) this.setLive(this.live); }

  now() { return Date.now() + this.offset; }

  private tableThing(id?: string): Thing | undefined {
    return this.h.furniture.find((t) => (id ? t.id === id : t.item === 'dining/round-set')) || this.h.furniture.find((t) => t.item === 'dining/round-set');
  }
  private floorY(t: Thing) { return (this.h.spec.floorH ?? FLOOR) + (t.level || 0) * (this.h.spec.storyH ?? 2.75); }
  // a place in the world: [x, z, level] for a table-frame point
  private world(t: Thing, [lx, lz]: [number, number]): P {
    const r = FACING[t.facing || 'south'] || 0, c = Math.cos(r), s = Math.sin(r);
    return [t.at[0] + lx * c + lz * s, t.at[1] - lx * s + lz * c, t.level || 0];
  }
  /** where a diner sits (chair), stands to get there, and looks (the table's middle) */
  place(i: number): { stand: P; seat: P; face: P } | null {
    const t = this.tableThing(this.live?.dinner?.table);
    if (!t) return null;
    const [lx, lz] = PLACES[i % PLACES.length], k = 1.55 / Math.hypot(lx, lz);
    return { seat: this.world(t, [lx * 0.98, lz * 0.98]), stand: this.world(t, [lx * k, lz * k]), face: this.world(t, [0, 0]) };
  }
  /** the places round the table with room to sit (a table against a wall seats fewer),
   *  in order; everyone after those eats standing nearby */
  private seats(): number[] {
    const t = this.tableThing(this.live?.dinner?.table);
    if (!t) return [];
    return PLACES.map((_, i) => i).filter((i) => !this.fits || this.fits(this.place(i)!.seat, t.id));
  }

  setLive(live: { dinner?: DinnerLive | null; kitchen?: KitchenLive; acted?: any; now: number }) {
    this.live = live;
    this.offset = live.now - Date.now();
    const d = live.dinner;
    const t = d ? this.tableThing(d.table) : undefined;
    const diners = d ? 1 + d.guests.length : 0;
    const key = d && t ? `${d.at}|${d.on}|${d.dishes.join(',')}|${diners}|${t.at.join(',')}` : '';
    if (key === this.key) return;
    this.key = key;
    if (this.setting) { this.root.remove(this.setting); this.setting = null; }
    this.steamAt = [];
    // the fruit bowl steps aside while the table is laid
    const tableObj = this.findTable(t?.id);
    tableObj?.traverse((o) => { if (o.userData.centerpiece) o.visible = !d; });
    if (!d || !t) return;
    const g = new THREE.Group();
    const y = this.floorY(t), top = y + 0.767;
    g.position.set(t.at[0], 0, t.at[1]);
    g.rotation.y = FACING[t.facing || 'south'] || 0;
    const n = Math.max(2, Math.min(4, diners));
    const main = d.dishes[0] || 'soup', sweet = d.dishes.find((x) => /cake|tart|pie|shortbread|biscuit/.test(x));
    const free = this.seats();
    for (const i of free.slice(0, Math.min(n, free.length))) {
      const [lx, lz] = PLACES[i];
      const p = plate(d.on ? main : null);
      p.position.set(lx * 0.42, top, lz * 0.42);
      p.rotation.y = Math.atan2(lx, lz);
      g.add(p);
      if (d.on && food(main).kind === 'soup') this.steamAt.push(new THREE.Vector3(...this.worldV(t, [lx * 0.42, lz * 0.42], top + 0.06)));
      // more chairs for more guests, round the table
      if (i >= 2) { const c = chair({}); c.position.set(lx, y, lz); c.rotation.y = Math.atan2(-lx, -lz); g.add(c); }
    }
    if (d.on) {
      const cp = centrepiece(sweet && food(main).kind !== 'soup' ? sweet : main);
      cp.position.set(0, top, 0); g.add(cp);
      this.steamAt.push(new THREE.Vector3(...this.worldV(t, [0, 0], top + cp.userData.steam)));
      if (sweet && sweet !== main) { const c2 = centrepiece(sweet); c2.scale.setScalar(0.7); c2.position.set(0.22, top, -0.22); g.add(c2); }
      g.add(candle(-0.18, 0.2).translateY(top), candle(0.2, -0.17).translateY(top));
    }
    g.traverse((o: any) => { if (o.isMesh && !o.userData.noShadow) { o.castShadow = false; o.receiveShadow = true; } });
    this.setting = g;
    this.root.add(g);
  }
  private worldV(t: Thing, l: [number, number], y: number): [number, number, number] { const w = this.world(t, l); return [w[0], y, w[1]]; }

  private findTable(id?: string): THREE.Object3D | null {
    let found: THREE.Object3D | null = null;
    if (!id) return null;
    this.lot.traverse((o) => { if (!found && o.userData.thing?.id === id) found = o; });
    return found;
  }

  /** who's at the table, and what they're up to: seats the diners (`self`
   *  first) and puffs steam and hearts */
  update(dt: number, at: { l: Life; since: number }[], self: Life | null) {
    const now = this.now(), d = this.live?.dinner, diners = at.map((x) => x.l);
    // the first plazmi here measures the room round the table for everyone
    const who = self || at[0]?.l;
    if (who && !this.fits) { this.fits = (p, id) => who.seatFree(p, id); this.key = ''; if (this.live) this.setLive(this.live); }
    if (d && d.on && now < d.until) {
      const free = this.seats();
      at.forEach(({ l, since }, k) => {
        const p = k < free.length ? this.place(free[k]) : this.place(k % PLACES.length);
        const text = k === 0 && l === self ? 'having neighbours over for dinner' : 'having dinner';
        // whoever sat down more than a minute ago is already in its place when the page opens;
        // no chair with room for it: it eats standing by the table
        if (p) l.dine(p.stand, k < free.length ? p.seat : null, p.face, d.until, text, now - since > 60000);
      });
    } else if (self && this.live?.acted?.verb === 'dine' && now - this.live.acted.at < 90000) {
      // dine on its own: a quiet meal at the table
      const free = this.seats(), p = this.place(free[0] ?? 0);
      if (p) self.dine(p.stand, free.length ? p.seat : null, p.face, this.live.acted.at + 90000, 'eating at the round table');
    }
    this.puff -= dt; this.heart -= dt;
    if (!this.emit) return;
    if (this.puff < 0) {
      this.puff = 0.55;
      for (const s of this.steamAt) this.emit('steam', s);
      // a dish on the range, cooking
      const cooking = (this.live?.kitchen || []).some((k) => k.ready > now);
      if (cooking) {
        const r = this.h.furniture.find((t) => t.item === 'kitchen/range');
        if (r) this.emit('steam', new THREE.Vector3(r.at[0], this.floorY(r) + 1.0, r.at[1]));
      }
    }
    if (this.heart < 0 && d?.on) {
      this.heart = 2.5 + Math.random() * 2.5;
      const seated = diners.filter((l) => l.seated && l.root.visible);
      if (seated.length > 1) this.emit('hearts', seated[Math.floor(Math.random() * seated.length)].head, 1);
    }
  }
}
